import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { formatMalaysiaDateTimeStamp } from '../../utils/dateTime';
import { BarList, Table } from './StatistikParts';

// Statistik Aduan Keluarga (AK): the family-complaint counterpart of Statistik i-WARAN.
const emptySummary = {
    subtype_labels: {},
    stage_labels: {},
    ip_status_labels: {},
    totals: { by_subtype: {}, by_stage: {}, by_ip_status: {}, by_prosecution: {} },
    by_district: [],
    prosecution: [],
    by_offense: [],
    without_offense: 0,
    by_court: [],
};

const emptyFilters = { district: '', fromDate: '', toDate: '' };

const ComplaintAkStatistikReport = () => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem('token');
    const role = String(localStorage.getItem('role') || '').trim().toLowerCase();
    const isDistrictOfficer = role === 'pegawai_daerah';
    const [isLoading, setIsLoading] = useState(true);
    const [isExporting, setIsExporting] = useState(false);
    const [error, setError] = useState('');
    const [districtOptions, setDistrictOptions] = useState([]);
    const [summary, setSummary] = useState(emptySummary);
    const [filters, setFilters] = useState(emptyFilters);
    // Only the latest request may update the page; an older, slower response must not overwrite it.
    const latestRequestRef = useRef(0);

    useEffect(() => {
        if (!apiUrl) return;
        axios.get(`${apiUrl}/districts`)
            .then((response) => setDistrictOptions(response?.data?.data || []))
            .catch(() => setDistrictOptions([]));
    }, [apiUrl]);

    const params = useMemo(() => ({
        ...(filters.district ? { district_id: filters.district } : {}),
        ...(filters.fromDate ? { from_date: filters.fromDate } : {}),
        ...(filters.toDate ? { to_date: filters.toDate } : {}),
    }), [filters]);

    const loadSummary = () => {
        if (!apiUrl) {
            setError('API URL tidak diset.');
            setIsLoading(false);
            return;
        }
        const requestId = latestRequestRef.current + 1;
        latestRequestRef.current = requestId;
        setIsLoading(true);
        setError('');
        axios.get(`${apiUrl}/complaints/report/statistik-ak`, {
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            params,
        })
            .then((response) => {
                if (requestId === latestRequestRef.current) setSummary({ ...emptySummary, ...(response?.data?.data || {}) });
            })
            .catch((err) => {
                if (requestId === latestRequestRef.current) setError(err?.response?.data?.message || 'Gagal memuatkan statistik Aduan Keluarga.');
            })
            .finally(() => {
                if (requestId === latestRequestRef.current) setIsLoading(false);
            });
    };

    useEffect(() => {
        loadSummary();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiUrl, params]);

    const handleExport = async () => {
        if (!apiUrl) return;
        setIsExporting(true);
        try {
            const response = await axios.get(`${apiUrl}/complaints/report/statistik-ak/export/xlsx`, {
                headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                params,
                responseType: 'blob',
            });
            const disposition = response?.headers?.['content-disposition'] || '';
            const match = disposition.match(/filename="?([^"]+)"?/i);
            const filename = match?.[1] || `statistik-ak-${formatMalaysiaDateTimeStamp()}.xlsx`;
            const url = window.URL.createObjectURL(new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setError('Gagal export Excel statistik Aduan Keluarga.');
        } finally {
            setIsExporting(false);
        }
    };

    const totals = summary.totals || emptySummary.totals;
    const bySubtype = totals.by_subtype || {};
    const byIpStatus = totals.by_ip_status || {};
    const byProsecution = totals.by_prosecution || {};
    // "Belum dipilih" only shows when some complaints have no subtype.
    const subtypeColumns = Object.entries(summary.subtype_labels || {})
        .filter(([key]) => key !== '' || Number(bySubtype[''] || 0) > 0);
    const ipColumns = Object.entries(summary.ip_status_labels || {});
    const stageRows = Object.entries(summary.stage_labels || {})
        .map(([key, label]) => ({ key, label, total: totals.by_stage?.[key] ?? 0 }));
    const offenseRows = summary.by_offense || [];
    const courtRows = summary.by_court || [];
    const sum = (rows, key) => rows.reduce((total, row) => total + Number(row[key] || 0), 0);
    const districtColumn = { key: 'district_name', label: 'Daerah', width: '1fr', render: (row) => row.district_name };
    const groupColumns = (entries, group, keyPrefix) => entries.map(([key, label]) => ({
        key: `${keyPrefix}-${key || 'none'}`,
        label,
        render: (row) => row[group]?.[key] ?? 0,
    }));
    const groupFooter = (entries, groupTotals, keyPrefix) => Object.fromEntries(
        entries.map(([key]) => [`${keyPrefix}-${key || 'none'}`, groupTotals[key] ?? 0])
    );

    return (
        <div className="app-section">
            <div className="app-section-header">
                <div>
                    <div className="app-section-eyebrow">PENGURUSAN LAPORAN</div>
                    <h2>Statistik Aduan Keluarga (AK)</h2>
                    <p>Subkategori, peringkat, status siasatan, temujanji, pendakwaan, seksyen kesalahan dan mahkamah bagi Aduan Keluarga.</p>
                </div>
                <Link className="app-button app-button-ghost" to="/app/complaints/report">
                    Kembali Dashboard
                </Link>
            </div>

            <div className="app-card">
                <div className="app-tab-panel">
                    <div className="app-form-grid">
                        {!isDistrictOfficer && (
                            <label className="app-form-field">
                                <span>Daerah</span>
                                <select id="ak-statistik-district" value={filters.district} onChange={(e) => setFilters((prev) => ({ ...prev, district: e.target.value }))}>
                                    <option value="">Semua</option>
                                    {districtOptions.map((d) => (
                                        <option key={d.id} value={d.id}>{d.name}</option>
                                    ))}
                                </select>
                            </label>
                        )}
                        <label className="app-form-field">
                            <span>Dari Tarikh Aduan</span>
                            <input id="ak-statistik-from" type="date" value={filters.fromDate} onChange={(e) => setFilters((prev) => ({ ...prev, fromDate: e.target.value }))} />
                        </label>
                        <label className="app-form-field">
                            <span>Hingga Tarikh Aduan</span>
                            <input id="ak-statistik-to" type="date" value={filters.toDate} onChange={(e) => setFilters((prev) => ({ ...prev, toDate: e.target.value }))} />
                        </label>
                        <div className="app-form-actions app-span-full">
                            <button type="button" className="app-button app-button-ghost" onClick={() => setFilters(emptyFilters)}>
                                Reset
                            </button>
                            <button type="button" className="app-button app-button-ghost" onClick={handleExport} disabled={isExporting}>
                                {isExporting ? 'Sedang Export...' : 'Export Excel'}
                            </button>
                            <button type="button" className="app-button" onClick={loadSummary}>
                                Refresh
                            </button>
                        </div>
                    </div>
                    <p className="app-muted" style={{ margin: '.5rem 0 0', fontSize: '.85rem' }}>
                        Aduan Keluarga ditapis mengikut tarikh aduan.{isDistrictOfficer ? ' Hanya aduan daerah anda dipaparkan.' : ''}
                    </p>

                    {error && <div className="app-empty">{error}</div>}
                    {isLoading && <div className="app-empty">Memuatkan statistik...</div>}

                    {!isLoading && !error && (
                        <div className="app-report" style={{ marginTop: '1rem' }}>
                            <div className="app-report-kpis">
                                <div className="app-report-kpi"><span>Jumlah Aduan Keluarga</span><strong>{totals.total ?? 0}</strong></div>
                                <div className="app-report-kpi"><span>Aduan dengan Temujanji</span><strong>{totals.with_appointment ?? 0}</strong></div>
                                <div className="app-report-kpi"><span>Siasatan Direkodkan</span><strong>{totals.investigated ?? 0}</strong></div>
                                <div className="app-report-kpi"><span>Dalam Siasatan (IP)</span><strong>{byIpStatus['Proses Siasatan'] ?? 0}</strong></div>
                                <div className="app-report-kpi"><span>IP Selesai</span><strong>{byIpStatus.Selesai ?? 0}</strong></div>
                                <div className="app-report-kpi"><span>Dalam Pendakwaan</span><strong>{byProsecution.didakwa ?? 0}</strong></div>
                            </div>

                            <div className="app-report-card">
                                <h4>Subkategori Aduan Keluarga Mengikut Daerah</h4>
                                <Table
                                    columns={[districtColumn, ...groupColumns(subtypeColumns, 'subtype', 'subtype'), { key: 'total', label: 'Jumlah' }]}
                                    rows={summary.by_district || []}
                                    rowKey={(row) => `subtype-${row.district_id || 'none'}`}
                                    footer={{ district_name: 'Jumlah', ...groupFooter(subtypeColumns, bySubtype, 'subtype'), total: totals.total ?? 0 }}
                                />
                            </div>

                            <div className="app-report-card">
                                <h4>Peringkat Aduan Keluarga</h4>
                                <BarList rows={stageRows} emptyText="Tiada Aduan Keluarga untuk tapisan semasa." />
                            </div>

                            <div className="app-report-card">
                                <h4>Status Siasatan (IP) Mengikut Daerah</h4>
                                <Table
                                    columns={[districtColumn, ...groupColumns(ipColumns, 'ip_status', 'ip'), { key: 'total', label: 'Jumlah' }]}
                                    rows={summary.by_district || []}
                                    rowKey={(row) => `ip-${row.district_id || 'none'}`}
                                    footer={{ district_name: 'Jumlah', ...groupFooter(ipColumns, byIpStatus, 'ip'), total: totals.total ?? 0 }}
                                />
                            </div>

                            <div className="app-report-card">
                                <h4>Temujanji dan Siasatan Mengikut Daerah</h4>
                                <Table
                                    columns={[
                                        districtColumn,
                                        { key: 'total', label: 'Aduan AK' },
                                        { key: 'with_appointment', label: 'Aduan dengan Temujanji' },
                                        { key: 'appointments', label: 'Jumlah Temujanji' },
                                        { key: 'investigated', label: 'Siasatan Direkodkan' },
                                    ]}
                                    rows={summary.by_district || []}
                                    rowKey={(row) => `appointment-${row.district_id || 'none'}`}
                                    footer={{
                                        district_name: 'Jumlah',
                                        total: totals.total ?? 0,
                                        with_appointment: totals.with_appointment ?? 0,
                                        appointments: totals.appointments ?? 0,
                                        investigated: totals.investigated ?? 0,
                                    }}
                                />
                            </div>

                            <div className="app-report-card">
                                <h4>Status Pendakwaan</h4>
                                <BarList rows={summary.prosecution || []} emptyText="Tiada Aduan Keluarga untuk tapisan semasa." />
                            </div>

                            <div className="app-report-card">
                                <h4>Aduan Keluarga Mengikut Seksyen Kesalahan</h4>
                                {offenseRows.length ? (
                                    <Table
                                        columns={[
                                            { key: 'section', label: 'Seksyen', width: '0.7fr', render: (row) => row.section || '-' },
                                            { key: 'name', label: 'Kesalahan', width: '1.6fr', render: (row) => row.name },
                                            { key: 'total', label: 'Bilangan', width: '0.35fr' },
                                        ]}
                                        rows={offenseRows}
                                        rowKey={(row) => `offense-${row.id}`}
                                        footer={{ name: 'Jumlah', total: sum(offenseRows, 'total') }}
                                    />
                                ) : (
                                    <div className="app-empty">Tiada Aduan Keluarga berseksyen untuk tapisan semasa.</div>
                                )}
                                {Number(summary.without_offense || 0) > 0 && (
                                    <p className="app-muted" style={{ margin: '.5rem 0 0', fontSize: '.85rem' }}>
                                        {summary.without_offense} aduan belum dipilih kesalahan dan tidak dikira dalam jadual ini.
                                    </p>
                                )}
                            </div>

                            <div className="app-report-card">
                                <h4>Pendakwaan Mengikut Mahkamah</h4>
                                {courtRows.length ? (
                                    <Table
                                        columns={[
                                            { key: 'name', label: 'Mahkamah', width: '1.6fr', render: (row) => row.name },
                                            { key: 'total', label: 'Bilangan', width: '0.4fr' },
                                        ]}
                                        rows={courtRows}
                                        rowKey={(row) => `court-${row.id}`}
                                        footer={{ name: 'Jumlah', total: sum(courtRows, 'total') }}
                                    />
                                ) : (
                                    <div className="app-empty">Belum ada Aduan Keluarga yang direkodkan mahkamah untuk tapisan semasa.</div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ComplaintAkStatistikReport;
